import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { TestSetService } from "../../services/TestSetService";
import { WorkflowService } from "../../services/WorkflowService";
import { Application, IntentGroup, TestSet } from "../../model_gen/evaluation";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { IntentService } from "../../services/IntentService";
import TestSetTypeStep, { TestSetType } from "./Steps/TestSetTypeStep";
import TestSetIntentGroup from "./Steps/TestSetIntentGroup";
import TestSetApplication from "./Steps/TestSetApplication";
import TestSetName from "./Steps/TestSetName";
import TestSetRequests from "./Steps/TestSetRequests";
import TestSetSessions from "./Steps/TestSetSessions";
import TestSetReview from "./Steps/TestSetReview";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { 
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  FileTextIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import { notification } from "antd";

interface TestSetData {
  name?: string;
  description?: string;
  requests?: ChatCompletionRequest[];
  is_golden: boolean;
  intentGroupId?: number;
  sourceType?: "manual" | "intent_group" | "dataset";
  sampleType?: "all" | "random";
  samplePercentage?: number;
  datasetId?: number;
  testSetType?: TestSetType;
  application?: Application | null;
  workflowItemSource?: "workflow_runs" | "sessions";
  workflowRunIds?: number[];
  sessionIds?: string[];
  sessionTransformWorkflowId?: number;
}

export const NEW_TEST_SET_STORAGE_KEY = "testSetData";

interface StepItem {
  key: string;
  title: string;
  component: React.ReactNode;
}

const TestSetNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [intentGroupLoading, setIntentGroupLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSetData, setTestSetData] = useState<TestSetData>(() => {
    const saved = localStorage.getItem(NEW_TEST_SET_STORAGE_KEY);
    
    // Get URL parameters for prepopulation
    const intentGroupId = searchParams.get("intent_group_id");
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          intentGroupId: intentGroupId ? parseInt(intentGroupId) : parsed.intentGroupId,
          requests: parsed.requests || [],
          is_golden: parsed.is_golden ?? false,
          sourceType: parsed.sourceType || "manual",
          sampleType: parsed.sampleType || "all",
          samplePercentage: parsed.samplePercentage ?? 100,
          datasetId: parsed.datasetId,
          testSetType: parsed.testSetType,
          workflowItemSource: parsed.workflowItemSource || "workflow_runs",
          workflowRunIds: parsed.workflowRunIds || [],
          sessionIds: parsed.sessionIds || [],
        };
      } catch (e) {
        console.error("Error parsing saved test set data", e);
      }
    }

    return { 
      requests: [],
      is_golden: false,
      intentGroupId: intentGroupId ? parseInt(intentGroupId) : undefined,
      sourceType: "manual",
      sampleType: "all",
      samplePercentage: 100,
      sessionIds: [],
      workflowRunIds: [],
      testSetType: intentGroupId ? "request" : undefined,
    };
  });

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(NEW_TEST_SET_STORAGE_KEY, JSON.stringify(testSetData));
  }, [testSetData]);

  // Update URL when step changes
  useEffect(() => {
    // Only update URL if step parameter should be present (when step > 0 or step was already in URL initially)
    const currentStepParam = searchParams.get("step");
    const shouldHaveStepParam = currentStep > 0 || currentStepParam !== null;
    
    if (shouldHaveStepParam && currentStepParam !== currentStep.toString()) {
      const currentParams = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...currentParams,
        step: currentStep.toString()
      }, { replace: true }); // Use replace to avoid creating new history entries for step changes
    }
  }, [currentStep, searchParams, setSearchParams]);

  // Fetch intent group data when intentGroupId changes
  useEffect(() => {
    if (testSetData.intentGroupId) {
      IntentService.getIntentGroup(testSetData.intentGroupId, false, setIntentGroupLoading, apiKey).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    } else {
      setIntentGroup(null);
    }
  }, [testSetData.intentGroupId, apiKey]);

  const steps: StepItem[] = useMemo(() => {
    const type = testSetData.testSetType;
    const base: StepItem[] = [
      {
        key: "type",
        title: "Type",
        component: (
          <TestSetTypeStep
            value={type}
            onChange={(t) =>
              setTestSetData((prev) => ({
                ...prev,
                testSetType: t,
                intentGroupId: t === "request" ? prev.intentGroupId : undefined,
                sessionIds: t === "workflow" ? prev.sessionIds || [] : undefined,
              }))
            }
          />
        ),
      },
    ];

    if (type === "request") {
      return [
        ...base,
        {
          key: "intent-group",
          title: "Intent Group",
          component: (
            <TestSetIntentGroup
              intentGroupId={testSetData.intentGroupId}
              onChange={(intentGroupId: number) =>
                setTestSetData((prev) => ({
                  ...prev,
                  intentGroupId,
                  requests: [],
                  sourceType: "manual",
                  sampleType: "all",
                  samplePercentage: 100,
                  datasetId: undefined,
                }))
              }
            />
          ),
        },
        {
          key: "details",
          title: "Details",
          component: (
            <TestSetName
              name={testSetData.name}
              description={testSetData.description}
              onChange={(name: string, description: string) =>
                setTestSetData((prev) => ({ ...prev, name, description }))
              }
            />
          ),
        },
        {
          key: "requests",
          title: "Requests",
          component: (
            <TestSetRequests
              intentGroupId={testSetData.intentGroupId!}
              testSetData={testSetData}
              onChange={(changes: Partial<TestSetData>) =>
                setTestSetData((prev) => ({ ...prev, ...changes }))
              }
            />
          ),
        },
        {
          key: "review",
          title: "Review",
          component: (
            <TestSetReview
              testSetData={testSetData}
              testSetType="request"
              apiKey={apiKey}
              onChange={(changes) =>
                setTestSetData((prev) => ({ ...prev, ...changes }))
              }
            />
          ),
        },
      ];
    }

    if (type === "workflow") {
      return [
        ...base,
        {
          key: "application",
          title: "Application",
          component: (
            <TestSetApplication
              application={testSetData.application ?? undefined}
              onChange={(app) =>
                setTestSetData((prev) => ({
                  ...prev,
                  application: app,
                  workflowItemSource: "workflow_runs",
                  workflowRunIds: [],
                  sessionIds: [],
                }))
              }
            />
          ),
        },
        {
          key: "details",
          title: "Details",
          component: (
            <TestSetName
              name={testSetData.name}
              description={testSetData.description}
              onChange={(name: string, description: string) =>
                setTestSetData((prev) => ({ ...prev, name, description }))
              }
            />
          ),
        },
        {
          key: "sessions",
          title: "Items",
          component: testSetData.application ? (
            <TestSetSessions
              application={testSetData.application}
              sourceType={testSetData.workflowItemSource || "workflow_runs"}
              selectedWorkflowRunIds={testSetData.workflowRunIds || []}
              selectedSessionIds={testSetData.sessionIds || []}
              onChange={(changes) =>
                setTestSetData((prev) => ({ ...prev, ...changes }))
              }
            />
          ) : (
            <p className="text-muted-foreground">
              Select an application in a previous step.
            </p>
          ),
        },
        {
          key: "review",
          title: "Review",
          component: (
            <TestSetReview
              testSetData={testSetData}
              testSetType="workflow"
              apiKey={apiKey}
              onChange={(changes) =>
                setTestSetData((prev) => ({ ...prev, ...changes }))
              }
            />
          ),
        },
      ];
    }

    return base;
  }, [testSetData, apiKey]);

  const canProceedFromStep = (stepIndex: number) => {
    const type = testSetData.testSetType;
    if (stepIndex === 0) return !!type;

    if (type === "request") {
      switch (stepIndex) {
        case 1:
          return !!testSetData.intentGroupId;
        case 2:
          return !!(testSetData.name && testSetData.name.trim().length > 0);
        case 3:
          if (testSetData.sourceType === "dataset") {
            const pct = testSetData.samplePercentage ?? 0;
            return !!testSetData.datasetId && pct > 0 && pct <= 100;
          }
          if (testSetData.sourceType === "intent_group") {
            const pct = testSetData.samplePercentage ?? 0;
            return pct > 0 && pct <= 100;
          }
          return !!(testSetData.requests && testSetData.requests.length > 0);
        case 4:
          return true;
        default:
          return false;
      }
    }

    if (type === "workflow") {
      switch (stepIndex) {
        case 1:
          return !!testSetData.application;
        case 2:
          return !!(testSetData.name && testSetData.name.trim().length > 0);
        case 3: {
          const hasRuns = !!(testSetData.workflowRunIds && testSetData.workflowRunIds.length > 0);
          const hasSessions = !!(testSetData.sessionIds && testSetData.sessionIds.length > 0);
          return hasRuns || hasSessions;
        }
        case 4:
          return true;
        default:
          return false;
      }
    }

    return false;
  };

  const canProceed = () => canProceedFromStep(currentStep);

  useEffect(() => {
    if (currentStep >= steps.length) {
      setCurrentStep(Math.max(0, steps.length - 1));
    }
  }, [steps.length, currentStep]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createTestSet = () => {
    const type = testSetData.testSetType;
    if (type === "workflow") {
      createWorkflowTestSet();
    } else {
      createRequestTestSet();
    }
  };

  const createRequestTestSet = () => {
    setLoading(true);
    const payload: Record<string, unknown> = {
      name: testSetData.name,
      description: testSetData.description,
      intent_group_id: testSetData.intentGroupId,
      is_golden: testSetData.is_golden,
    };

    if (testSetData.sourceType === "dataset") {
      payload.dataset_id = testSetData.datasetId;
      payload.sample_percentage = testSetData.samplePercentage ?? 100;
    } else if (testSetData.sourceType === "intent_group") {
      const pct = testSetData.samplePercentage ?? 100;
      payload.sample_type = pct >= 100 ? "all" : "random";
      payload.sample_percentage = pct;
    } else {
      payload.requests =
        testSetData.requests?.map((request) => ({
          request_id: request.request_id,
          metadata: request.metadata || {},
        })) || [];
    }

    TestSetService.createTestSet(payload, setLoading, apiKey).subscribe(
      (response: TestSet) => {
        localStorage.removeItem(NEW_TEST_SET_STORAGE_KEY);
        notification.success({
          message: "Test set created successfully",
          description: "Your test set has been created successfully",
        });
        navigate(`/intent-group/${testSetData.intentGroupId}/testsets/${response.id}`);
      },
      (error: any) => {
        console.error("Error creating test set:", error);
        notification.error({
          message: "Error creating test set",
          description: error.message || "An error occurred while creating the test set",
        });
        setLoading(false);
      }
    );
  };

  const createWorkflowTestSet = () => {
    const app = testSetData.application;
    if (!app?.id || !testSetData.name) return;

    const workflowRunIds = testSetData.workflowRunIds || [];
    const sessionIds = testSetData.sessionIds || [];

    if (workflowRunIds.length === 0 && sessionIds.length === 0) return;

    setLoading(true);
    WorkflowService.createWorkflowTestSet(
      app.id,
      testSetData.name,
      workflowRunIds.length > 0 ? workflowRunIds : null,
      sessionIds.length > 0 ? sessionIds : null,
      testSetData.sessionTransformWorkflowId ?? null,
      testSetData.description,
      setLoading,
      apiKey
    ).subscribe(
      (response: { test_set?: { id: number }; id?: number }) => {
        localStorage.removeItem(NEW_TEST_SET_STORAGE_KEY);
        notification.success({
          message: "Test set created successfully",
          description: "Your workflow test set has been created successfully",
        });
        navigate("/test/test-sets");
      },
      (error: any) => {
        console.error("Error creating workflow test set:", error);
        notification.error({
          message: "Error creating test set",
          description: error.message || "An error occurred while creating the test set",
        });
        setLoading(false);
      }
    );
  };

  const completedSteps = steps.slice(0, currentStep).filter((_, index) => {
    return canProceedFromStep(index);
  }).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/test/test-sets")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Test Sets
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">New Test Set</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                New Test Set
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Create a new test set to evaluate model performance
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Progress Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-gc-med text-foreground">
                    Step {currentStep + 1} of {steps.length}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {steps[currentStep].title}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-gc-med text-foreground">
                    {completedSteps}/{steps.length} complete
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          index < currentStep ? "bg-maitai-green" : 
                          index === currentStep ? "bg-maitai-green/60" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Step Navigation */}
              <div className="flex items-center justify-center flex-wrap gap-x-2 gap-y-3">
                {steps.map((step, index) => (
                  <React.Fragment key={step.key}>
                    <div
                      className={cn(
                        "flex items-center gap-3 text-sm transition-colors flex-shrink-0",
                        index === currentStep
                          ? "text-maitai-green"
                          : index < currentStep
                          ? "text-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      <div
                        className={cn(
                          "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                          index === currentStep
                            ? "bg-maitai-green text-white"
                            : index < currentStep
                            ? "bg-maitai-green text-white"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        {index < currentStep ? (
                          <CheckIcon className="w-3 h-3" />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                      <span className="font-gc-reg">{step.title}</span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={cn(
                          "w-6 h-0.5 flex-shrink-0 rounded",
                          index < currentStep ? "bg-maitai-green" : "bg-muted"
                        )}
                        aria-hidden
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {steps[currentStep].component}
          </CardContent>
        </Card>

        {/* Navigation Card */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={loading}
                    className="gap-2"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous Step
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className="gap-2"
                  >
                    Continue
                    <ChevronRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    disabled={!canProceed() || loading}
                    onClick={createTestSet}
                    className="gap-2"
                  >
                    {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    {loading ? "Creating test set..." : (
                      <>
                        <FileTextIcon className="w-4 h-4" />
                        Create Test Set
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestSetNew;
